import { useState, useEffect } from "react";
import { api, SALES_CHANNELS, channelStatusBadge } from "@/lib/api";
import { toast } from "sonner";
import { Store, DollarSign, Loader2, Send, Check, ExternalLink, Factory } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

const STATUS_LABEL = { published: "Live", draft: "Draft", not_listed: "Not listed" };
const FIELDS = [
  ["retail_price", "Retail", "$"], ["msrp", "MSRP", "$"], ["wholesale_price", "Wholesale", "$"],
  ["moq", "Faire MOQ", ""], ["case_pack", "Case pack", ""],
];

export function ChannelPublish({ product, onUpdate }) {
  const [pricing, setPricing] = useState({});
  const [savingPrice, setSavingPrice] = useState(false);
  const [shopifyConnected, setShopifyConnected] = useState(null);
  const [printifyConfigured, setPrintifyConfigured] = useState(false);
  const [publishingPrintify, setPublishingPrintify] = useState(false);

  useEffect(() => {
    api.get("/channels").then(({ data }) => {
      setShopifyConnected(!!data.shopify_connected);
      setPrintifyConfigured(!!data.printify_configured);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    setPricing({
      retail_price: product.retail_price ?? product.suggested_price ?? 0,
      msrp: product.msrp ?? product.suggested_price ?? 0,
      wholesale_price: product.wholesale_price ?? 0,
      moq: product.moq ?? 6,
      case_pack: product.case_pack ?? 1,
    });
  }, [product.id]);

  const savePricing = async () => {
    setSavingPrice(true);
    try {
      const { data } = await api.patch(`/products/${product.id}`, {
        retail_price: Number(pricing.retail_price), msrp: Number(pricing.msrp),
        wholesale_price: Number(pricing.wholesale_price), moq: Number(pricing.moq),
        case_pack: Number(pricing.case_pack),
      });
      onUpdate(data);
      toast.success("Pricing saved");
    } finally { setSavingPrice(false); }
  };

  const publishPrintify = async () => {
    const alreadyLive = printifyChan?.status === "published";
    if (alreadyLive && !window.confirm("This product is already live on Shopify via Printify. Publishing again will create a DUPLICATE listing. Continue anyway?")) return;
    setPublishingPrintify(true);
    try {
      const { data } = await api.post(`/products/${product.id}/publish-printify`, { public_base: window.location.origin, force: alreadyLive });
      onUpdate(data);
      toast.success(alreadyLive ? "Re-published to Printify" : "Sent to Printify — syncing to your Shopify store");
    } catch (e) { toast.error(e?.response?.data?.detail || "Printify publish failed"); }
    finally { setPublishingPrintify(false); }
  };

  const chan = product.channels || {};
  const printifyChan = chan.printify;

  return (
    <div className="p-5 border-b border-zinc-200" data-testid="channel-publish">
      {printifyConfigured && (
        <div className="mb-5 rounded-lg border border-orange-200 bg-orange-50/60 p-3.5" data-testid="printify-panel">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold flex items-center gap-1.5 text-orange-900"><Factory className="w-4 h-4" /> Print-on-demand via Printify</div>
              <p className="text-[11px] text-orange-800/80 mt-0.5">Creates a fulfillable product in Printify and auto-syncs it to your Shopify store. Refine the artwork in Printify before it goes live.</p>
              {printifyChan?.url && (
                <a href={printifyChan.url} target="_blank" rel="noreferrer" data-testid="printify-editor-link" className="text-[11px] text-orange-700 font-medium flex items-center gap-1 mt-1.5">Open in Printify editor <ExternalLink className="w-2.5 h-2.5" /></a>
              )}
            </div>
            <button onClick={publishPrintify} disabled={publishingPrintify} data-testid="publish-printify-btn" className={`shrink-0 h-9 px-3.5 rounded-md disabled:opacity-60 text-white text-sm font-medium flex items-center gap-1.5 ${printifyChan?.status === "published" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-orange-600 hover:bg-orange-700"}`}>
              {publishingPrintify ? <Loader2 className="w-4 h-4 animate-spin" /> : (printifyChan?.status === "published" ? <Check className="w-4 h-4" /> : <Factory className="w-4 h-4" />)} {printifyChan?.status === "published" ? "Live on Shopify" : (printifyChan ? "Re-send" : "Send to Printify")}
            </button>
          </div>
        </div>
      )}

      <div className="overline text-zinc-400 mb-2 flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5" /> Retail &amp; wholesale pricing</div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {FIELDS.map(([k, label, pre]) => (
          <label key={k} className="block">
            <span className="text-[10px] text-zinc-400">{label}</span>
            <div className="relative">
              {pre && <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-400 text-xs">{pre}</span>}
              <Input type="number" min="0" value={pricing[k] ?? ""} onChange={(e) => setPricing((p) => ({ ...p, [k]: e.target.value }))} className={`h-9 text-sm ${pre ? "pl-5" : ""}`} data-testid={`price-${k}`} />
            </div>
          </label>
        ))}
      </div>
      <button onClick={savePricing} disabled={savingPrice} data-testid="save-pricing-btn" className="mt-2 text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1.5">
        {savingPrice ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />} Save pricing
      </button>

      <div className="overline text-zinc-400 mt-5 mb-2 flex items-center gap-1.5"><Store className="w-3.5 h-3.5" /> Distribution</div>
      <div className="rounded-lg border border-zinc-200 bg-zinc-50/70 p-3.5" data-testid="distribution-note">
        <p className="text-[11px] text-zinc-600 leading-relaxed">
          Publishing via <b>Send to Printify</b> above syncs this product live to your <b>Shopify</b> store — your single hub.
          From there, <b>TikTok Shop, Etsy &amp; Faire</b> are distributed automatically through Shopify's native Sales Channels.
          Connect those inside your Shopify admin (see <b>Distribution</b>).
        </p>
        {shopifyConnected === false && (
          <p className="text-[11px] text-amber-600 mt-1.5">Shopify isn't connected yet — connect it in Distribution to go live.</p>
        )}
      </div>
    </div>
  );
}
